import PersistentStore from "@/api/PersistentStore";
import { ErrorTypes } from "@/api/request";
import Resolver from "@/api/resolver";
import { TIME_ONLY_FORMAT, ZERO_TIME } from "@/components/shared/util";
import { localNow, parseServerTime } from "@/filters/DateAndTime";
import _ from "lodash";
import Vue from "vue";
import { MutationTree } from "vuex";
import { initialState, TOWER_MODE_KEY } from ".";
import { AudioAlertTracker, Patient, PatientState } from "./types";

export const mutations: MutationTree<PatientState> = {
  reset(state: Record<string, any>) {
    const s: Record<string, any> = initialState();
    Object.keys(s).forEach(key => {
      state[key] = s[key];
    });
  },
  censusUpdate(state: PatientState, patients: Array<Patient>) {
    onSuccess(state);
    const bodyIndex = _.invert(
      patients
        .filter(patient => patient.body)
        .map(patient => {
          return patient.id;
        })
    );
    patients.forEach(patient => {
      if (bodyIndex[patient.id] && state.patientList[+bodyIndex[patient.id]]) {
        patient.body = state.patientList[+bodyIndex[patient.id]].body;
      }
    });

    state.patientList = patients;
  },
  censusLoaded(state, payload) {
    state.censusLoaded = payload;
  },
  error(state, payload) {
    state.error = payload.errorType;
    state.errorStack = payload.error.stack;
    state.errorMessage = payload.error.message;
    state.errorUrl = _.get(
      payload,
      "error.config.url",
      "Config Object Was not defined"
    );
  },
  // Kept in sync by hook listening to the router
  setId(state: PatientState, { id }) {
    if (id !== state.activePatientId) {
      state.activePatientId = id;
    }
  },
  bodyUpdate(state: PatientState, { id, census, body }) {
    // id must be an integer
    id = +id;

    formatCaseReview(body);

    onSuccess(state);
    const patientIndex = _.findIndex(state.patientList, { id });

    if (patientIndex !== -1) {
      const patient = state.patientList[patientIndex];
      Vue.set(patient, "body", body);
      Vue.set(patient, "census", census);
    } else {
      state.patientList.push({
        id,
        census,
        body
      });
    }
  },
  updateMedList(state: PatientState, meds) {
    state.medList = meds;
  },
  updateCareCoordinationNote(state: PatientState, note) {
    state.careNote = note;
  },
  setVars(state: PatientState, { vars, area }) {
    onSuccess(state);
    Vue.set(state.vars, area, vars);
  },
  isTesting(state: PatientState, isTesting) {
    state.isTesting = isTesting;
  },
  isActiveTest(state: PatientState, isActiveTesting) {
    state.isActiveTesting = isActiveTesting;
  },
  isPagingTest(state: PatientState, isPagingTest) {
    state.isPagingTest = isPagingTest;
  },
  isPagingTestActive(state: PatientState, isPagingTestActive) {
    state.isPagingTestActive = isPagingTestActive;
  },
  now(state: PatientState, { now, dateTimeOffset }) {
    state.stats.now = now;
    state.stats.dateTimeOffset = dateTimeOffset;
    syncServerTime(state);
  },
  stats(state: PatientState, stats) {
    state.stats = stats;
    syncServerTime(state);
  },
  toggleTower(state: PatientState) {
    state.towerMode = !state.towerMode;
    PersistentStore.setItem(TOWER_MODE_KEY, state.towerMode);
  },
  toggleVerify(state: PatientState) {
    state.verifyMode = !state.verifyMode;
  },
  singlePatientView(state: PatientState) {
    state.singlePatientView = true;
  },

  setAudioAlerts(state: PatientState, patients: Patient[]) {
    const newAudioAlerts: Record<string, AudioAlertTracker> = {};
    const previousAudioAlerts = state.audioAlerts;
    patients.forEach(patient => {
      // removing old audio alerts that are no longer active
      // TODO: Only match patient when removing?
      patient.census.alert
        .filter(alert => alert.audioMessage)
        .forEach(alert => {
          const key = `${patient.id}_${alert.id}`;
          newAudioAlerts[key] = {
            alert,
            patient,
            firstReadTime: null,
            readCount: 0
          };

          if (previousAudioAlerts[key]) {
            newAudioAlerts[key].firstReadTime =
              previousAudioAlerts[key].firstReadTime;
            newAudioAlerts[key].readCount = previousAudioAlerts[key].readCount;
          }
        });
    });
    state.audioAlerts = newAudioAlerts;
  },

  markRead(state: PatientState, audioAlertKey: string) {
    const audioAlert = state.audioAlerts[audioAlertKey];
    if (!audioAlert.firstReadTime) {
      audioAlert.firstReadTime = localNow();
    }
    audioAlert.readCount++;
  },
  varsLoaded(state: PatientState, payload) {
    state.varsLoaded = payload;
  }
};

function formatCaseReview(body: any) {
  if (body.caseReview && body.caseReview.contact) {
    body.caseReview.contact = body.caseReview.contact.map((contact: any) => {
      if (contact.sent) {
        contact.sent = parseServerTime(contact.sent).format(TIME_ONLY_FORMAT);
      }
      if (contact.response) {
        if (contact.response === ZERO_TIME) {
          contact.response = "";
        } else {
          contact.response = parseServerTime(contact.response).format(
            TIME_ONLY_FORMAT
          );
        }
      }
      return contact;
    });
  }
}

function syncServerTime(state: PatientState) {
  if (Resolver.demo) {
    return;
  }
  const dateTimeOffset = getDateTimeOffset(state.stats.dateTimeOffset);
  const offsetInMinutes = state.stats.dateTimeOffset;
  // No offset exists yet, need to pass false to avoid adding +00:00 and manualy add it
  const now = state.stats.now + dateTimeOffset;
  Resolver.setup({ now, dateTimeOffset, offsetInMinutes });
}

function pad(num: number) {
  var norm = Math.floor(Math.abs(num));
  return (norm < 10 ? "0" : "") + norm;
}

function getDateTimeOffset(offset: number) {
  const diff = offset >= 0 ? "+" : "-";
  const hours = pad(offset / 60);
  const minutes = pad(offset % 60);
  // Do not include : between hours and minutes for IE compatibility
  return `${diff}${hours}${minutes}`;
}

function onSuccess(state: PatientState) {
  state.error = ErrorTypes.NONE;
}
